import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Minus,
  Plus,
  Trash,
  ShoppingBag,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { useCart } from "@/context/CartContext";

/* =========================================================
   HELPERS
========================================================= */

const inr = (value) => {
  const number = Number(value);

  return `₹${(
    Number.isFinite(number) ? number : 0
  ).toLocaleString("en-IN")}`;
};

/*
 * Safely convert a possible stock value to a number.
 *
 * Returns:
 * - null = stock is not specified
 * - number = valid stock value
 */
const getNumericStock = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : null;
};

/* =========================================================
   CART DRAWER
========================================================= */

export default function CartDrawer() {
  const navigate = useNavigate();

  const {
    items = [],
    open,
    setOpen,
    updateQty,
    removeItem: removeFromCart,
    clearCart: clearContextCart,
  } = useCart();

  /* =======================================================
     ITEM PRICE
  ======================================================= */

  const getItemPrice = (item) => {
    /*
     * Preferred:
     * selected variant price.
     */
    if (
      item?.selectedVariant?.price !==
        undefined &&
      item?.selectedVariant?.price !== null &&
      item?.selectedVariant?.price !== ""
    ) {
      const price = Number(
        item.selectedVariant.price
      );

      if (Number.isFinite(price)) {
        return price;
      }
    }

    /*
     * Backward compatibility:
     * variant.price
     */
    if (
      item?.variant?.price !==
        undefined &&
      item?.variant?.price !== null &&
      item?.variant?.price !== ""
    ) {
      const price = Number(
        item.variant.price
      );

      if (Number.isFinite(price)) {
        return price;
      }
    }

    /*
     * Fallback:
     * product/cart item price.
     */
    const price = Number(item?.price);

    return Number.isFinite(price)
      ? price
      : 0;
  };

  /* =======================================================
     ITEM KEY
  ======================================================= */

  const getItemKey = (item) => {
    /*
     * Prefer the exact key generated by CartContext.
     */
    if (item?.key) {
      return item.key;
    }

    if (item?.cartKey) {
      return item.cartKey;
    }

    /*
     * Fallback for older cart entries.
     */
    const productId =
      item?.product_id ||
      item?.productId ||
      item?.id ||
      "";

    const size = String(
      item?.size ||
        item?.selectedVariant?.size ||
        item?.variant?.size ||
        ""
    ).toLowerCase();

    return `${productId}-${size}`;
  };

  /* =======================================================
     ITEM COUNT
  ======================================================= */

  const totalItems = useMemo(() => {
    return items.reduce(
      (sum, item) => {
        const quantity =
          Number(item?.quantity) || 0;

        return sum + quantity;
      },
      0
    );
  }, [items]);

  /* =======================================================
     SUBTOTAL
  ======================================================= */

  const subtotal = useMemo(() => {
    return items.reduce(
      (sum, item) => {
        const price =
          getItemPrice(item);

        const quantity =
          Number(item?.quantity) || 0;

        return (
          sum +
          price * quantity
        );
      },
      0
    );
  }, [items]);

  /* =======================================================
     CLOSE DRAWER
  ======================================================= */

  const closeDrawer = () => {
    setOpen(false);
  };

  /* =======================================================
     UPDATE QUANTITY
  ======================================================= */

  const changeQuantity = (
    item,
    delta
  ) => {
    const key =
      getItemKey(item);

    if (!key) {
      toast.error(
        "Unable to update this cart item"
      );

      return;
    }

    const currentQuantity =
      Number(item?.quantity) || 1;

    const quantityChange =
      Number(delta) || 0;

    const requestedQuantity =
      currentQuantity +
      quantityChange;

    /*
     * Never allow quantity below 1.
     */
    if (requestedQuantity < 1) {
      return;
    }

    /*
     * Determine stock from the selected
     * variant first.
     *
     * This is important for products where
     * S/M/L have different stock quantities.
     */
    const rawStock =
      item?.selectedVariant?.stock ??
      item?.variant?.stock ??
      item?.stock;

    const numericStock =
      getNumericStock(rawStock);

    /*
     * If stock information exists,
     * enforce it.
     */
    if (numericStock !== null) {
      /*
       * Out of stock.
       */
      if (numericStock <= 0) {
        toast.error(
          "This item is currently out of stock"
        );

        return;
      }

      /*
       * Cannot exceed available stock.
       */
      if (
        requestedQuantity >
        numericStock
      ) {
        toast.error(
          `Only ${numericStock} available in stock`
        );

        return;
      }
    }

    /*
     * CartContext expects a delta,
     * not the final quantity.
     */
    updateQty(
      key,
      quantityChange
    );
  };

  /* =======================================================
     REMOVE ITEM
  ======================================================= */

  const removeItem = (item) => {
    const key =
      getItemKey(item);

    if (!key) {
      toast.error(
        "Unable to remove this item"
      );

      return;
    }

    removeFromCart(key);

    toast.success(
      "Item removed from cart"
    );
  };

  /* =======================================================
     CLEAR CART
  ======================================================= */

  const clearCart = () => {
    if (!items.length) {
      return;
    }

    clearContextCart();

    toast.success(
      "Cart cleared"
    );
  };

  /* =======================================================
     CHECKOUT
  ======================================================= */

  const goToCheckout = () => {
    if (!items.length) {
      toast.error(
        "Your cart is empty"
      );

      return;
    }

    /*
     * Close drawer before navigating.
     */
    setOpen(false);

    /*
     * Your checkout route.
     */
    navigate("/checkout");
  };

  /* =======================================================
     CONTINUE SHOPPING
  ======================================================= */

  const continueShopping = () => {
    setOpen(false);

    navigate("/shop");
  };

  /* =======================================================
     CLOSED
  ======================================================= */

  if (!open) {
    return null;
  }

  /* =======================================================
     DRAWER
  ======================================================= */

  return (
    <div className="fixed inset-0 z-[100]">

      {/* ===================================================
          BACKDROP
      =================================================== */}

      <button
        type="button"
        aria-label="Close cart"
        onClick={closeDrawer}
        className="
          absolute
          inset-0
          bg-black/40
          backdrop-blur-[1px]
        "
      />

      {/* ===================================================
          DRAWER PANEL
      =================================================== */}

      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
        className="
          absolute
          right-0
          top-0
          h-full
          w-full
          sm:w-[440px]
          bg-background
          border-l
          border-border
          shadow-2xl
          flex
          flex-col
        "
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <div
          className="
            flex
            items-center
            justify-between
            px-5
            py-5
            border-b
            border-border
            shrink-0
          "
        >
          <div>

            <div className="flex items-center gap-2">

              <ShoppingBag
                size={20}
                weight="light"
              />

              <h2 className="text-xl tracking-tight">
                Your Cart
              </h2>

            </div>

            <p className="text-xs text-muted-foreground mt-1">
              {totalItems}{" "}
              {totalItems === 1
                ? "item"
                : "items"}
            </p>

          </div>

          <button
            type="button"
            onClick={closeDrawer}
            className="
              p-2
              hover:bg-secondary
              transition-colors
            "
            aria-label="Close cart"
          >
            <X size={22} />
          </button>

        </div>

        {/* =================================================
            CONTENT
        ================================================= */}

        <div
          className="
            flex-1
            overflow-y-auto
          "
        >

          {/* =================================================
              EMPTY CART
          ================================================= */}

          {!items.length && (

            <div
              className="
                h-full
                flex
                flex-col
                items-center
                justify-center
                text-center
                px-8
              "
            >

              <div
                className="
                  w-16
                  h-16
                  border
                  border-border
                  rounded-full
                  flex
                  items-center
                  justify-center
                  mb-5
                "
              >
                <ShoppingBag
                  size={28}
                  weight="light"
                />
              </div>

              <h3 className="text-lg">
                Your cart is empty
              </h3>

              <p
                className="
                  text-sm
                  text-muted-foreground
                  mt-2
                  max-w-xs
                "
              >
                Add school uniforms and
                other products to your cart
                and they will appear here.
              </p>

              <button
                type="button"
                onClick={
                  continueShopping
                }
                className="
                  mt-6
                  rounded-full
                  bg-foreground
                  text-background
                  px-5
                  py-3
                  text-xs
                  uppercase
                  tracking-widest
                  hover:bg-accent
                  transition-colors
                "
              >
                Continue shopping
              </button>

            </div>
          )}

          {/* =================================================
              CART ITEMS
          ================================================= */}

          {items.length > 0 && (

            <div className="p-5 space-y-4">

              {/* CLEAR CART */}

              <div className="flex justify-end">

                <button
                  type="button"
                  onClick={clearCart}
                  className="
                    text-xs
                    uppercase
                    tracking-widest
                    text-muted-foreground
                    hover:text-foreground
                    transition-colors
                  "
                >
                  Clear cart
                </button>

              </div>

              {/* ITEMS */}

              {items.map((item) => {

                const key =
                  getItemKey(item);

                const price =
                  getItemPrice(item);

                const quantity =
                  Math.max(
                    1,
                    Number(item?.quantity) || 1
                  );

                const itemTotal =
                  price * quantity;

                /*
                 * Image fallback chain.
                 */
                const image =
                  item?.image ||
                  item?.images?.[0] ||
                  item?.product_image ||
                  item?.productImage ||
                  "";

                /*
                 * Product name fallback chain.
                 */
                const productName =
                  item?.name ||
                  item?.product_name ||
                  item?.productName ||
                  "Product";

                /*
                 * Selected size fallback chain.
                 */
                const size =
                  item?.size ||
                  item?.selected_size ||
                  item?.selectedVariant?.size ||
                  item?.variant?.size ||
                  "";

                /*
                 * Determine selected variant stock
                 * for display / quantity behavior.
                 */
                const rawStock =
                  item?.selectedVariant?.stock ??
                  item?.variant?.stock ??
                  item?.stock;

                const numericStock =
                  getNumericStock(rawStock);

                const isOutOfStock =
                  numericStock !== null &&
                  numericStock <= 0;

                const isAtStockLimit =
                  numericStock !== null &&
                  quantity >= numericStock;

                return (
                  <div
                    key={key}
                    className="
                      border
                      border-border
                      p-3
                    "
                  >

                    <div className="flex gap-3">

                      {/* IMAGE */}

                      <div
                        className="
                          w-20
                          h-24
                          bg-secondary
                          shrink-0
                          overflow-hidden
                        "
                      >

                        {image ? (

                          <img
                            src={image}
                            alt={productName}
                            className="
                              w-full
                              h-full
                              object-cover
                            "
                          />

                        ) : (

                          <div
                            className="
                              w-full
                              h-full
                              flex
                              items-center
                              justify-center
                            "
                          >
                            <ShoppingBag
                              size={22}
                              className="text-muted-foreground"
                            />
                          </div>

                        )}

                      </div>

                      {/* DETAILS */}

                      <div
                        className="
                          flex-1
                          min-w-0
                        "
                      >

                        <div
                          className="
                            flex
                            justify-between
                            gap-3
                          "
                        >

                          <div className="min-w-0">

                            <h3
                              className="
                                text-sm
                                font-medium
                                truncate
                              "
                            >
                              {productName}
                            </h3>

                            {item?.category && (

                              <p
                                className="
                                  text-xs
                                  text-muted-foreground
                                  mt-0.5
                                "
                              >
                                {item.category}
                              </p>

                            )}

                          </div>

                          {/* REMOVE */}

                          <button
                            type="button"
                            onClick={() =>
                              removeItem(item)
                            }
                            className="
                              text-muted-foreground
                              hover:text-destructive
                              transition-colors
                              shrink-0
                            "
                            aria-label={`Remove ${productName}`}
                          >
                            <Trash size={16} />
                          </button>

                        </div>

                        {/* SCHOOL */}

                        {item?.school_name && (

                          <p
                            className="
                              text-xs
                              text-muted-foreground
                              mt-2
                            "
                          >
                            {item.school_name}
                          </p>

                        )}

                        {/* SIZE */}

                        {size && (

                          <div className="mt-2">

                            <span
                              className="
                                text-xs
                                border
                                border-border
                                px-2
                                py-1
                              "
                            >
                              Size:{" "}
                              <strong>
                                {size}
                              </strong>
                            </span>

                          </div>

                        )}

                        {/* PRICE */}

                        <p className="text-sm mt-3">
                          {inr(price)}
                        </p>

                        {/* OUT OF STOCK MESSAGE */}

                        {isOutOfStock && (

                          <p
                            className="
                              text-xs
                              text-destructive
                              mt-2
                            "
                          >
                            Currently out of stock
                          </p>

                        )}

                        {/* QUANTITY */}

                        <div
                          className="
                            flex
                            items-center
                            justify-between
                            mt-3
                          "
                        >

                          <div
                            className="
                              flex
                              items-center
                              border
                              border-border
                            "
                          >

                            {/* MINUS */}

                            <button
                              type="button"
                              onClick={() =>
                                changeQuantity(
                                  item,
                                  -1
                                )
                              }
                              disabled={
                                quantity <= 1 ||
                                isOutOfStock
                              }
                              className="
                                w-8
                                h-8
                                flex
                                items-center
                                justify-center
                                hover:bg-secondary
                                transition-colors
                                disabled:opacity-40
                                disabled:cursor-not-allowed
                              "
                              aria-label={`Decrease quantity of ${productName}`}
                            >
                              <Minus size={13} />
                            </button>

                            {/* QUANTITY */}

                            <span
                              className="
                                w-9
                                text-center
                                text-sm
                              "
                            >
                              {quantity}
                            </span>

                            {/* PLUS */}

                            <button
                              type="button"
                              onClick={() =>
                                changeQuantity(
                                  item,
                                  1
                                )
                              }
                              disabled={
                                isOutOfStock ||
                                isAtStockLimit
                              }
                              className="
                                w-8
                                h-8
                                flex
                                items-center
                                justify-center
                                hover:bg-secondary
                                transition-colors
                                disabled:opacity-40
                                disabled:cursor-not-allowed
                              "
                              aria-label={`Increase quantity of ${productName}`}
                            >
                              <Plus size={13} />
                            </button>

                          </div>

                          {/* ITEM TOTAL */}

                          <p
                            className="
                              text-sm
                              font-medium
                            "
                          >
                            {inr(itemTotal)}
                          </p>

                        </div>

                        {/* STOCK INFO */}

                        {numericStock !== null &&
                          numericStock > 0 && (

                          <p
                            className="
                              text-[11px]
                              text-muted-foreground
                              mt-2
                            "
                          >
                            {numericStock}{" "}
                            {numericStock === 1
                              ? "available"
                              : "available"}
                          </p>

                        )}

                      </div>

                    </div>

                  </div>
                );
              })}

            </div>
          )}

        </div>

        {/* =================================================
            FOOTER
        ================================================= */}

        {items.length > 0 && (

          <div
            className="
              border-t
              border-border
              bg-background
              p-5
              shrink-0
            "
          >

            {/* SUBTOTAL */}

            <div
              className="
                flex
                justify-between
                items-center
                mb-2
              "
            >

              <span
                className="
                  text-sm
                  text-muted-foreground
                "
              >
                Subtotal
              </span>

              <span className="text-lg font-medium">
                {inr(subtotal)}
              </span>

            </div>

            <p
              className="
                text-xs
                text-muted-foreground
                mb-4
              "
            >
              Taxes, shipping and any applicable
              discounts are calculated at checkout.
            </p>

            {/* CHECKOUT */}

            <button
              type="button"
              onClick={goToCheckout}
              className="
                w-full
                rounded-full
                bg-foreground
                text-background
                px-5
                py-3
                text-xs
                uppercase
                tracking-widest
                hover:bg-accent
                transition-colors
              "
            >
              Proceed to checkout
            </button>

            {/* CONTINUE SHOPPING */}

            <button
              type="button"
              onClick={
                continueShopping
              }
              className="
                w-full
                mt-2
                rounded-full
                border
                border-border
                px-4
                py-3
                text-xs
                uppercase
                tracking-widest
                hover:bg-secondary
                transition-colors
              "
            >
              Continue shopping
            </button>

          </div>
        )}

      </aside>

    </div>
  );
}
